import { GlobalFilterSelection } from '../global-filter-modal/global-filter-modal.component';

export const DEFAULT_VISUAL_ORDER = 1;
export const DEFAULT_VISUAL_WIDTH = 12;
export const MIN_VISUAL_WIDTH = 3;
export const MAX_VISUAL_WIDTH = 12;

export interface VisualDatasetColumn {
  key: string;
  name?: string;
  alias?: string;
  type?: string;
  is_primary_key?: boolean;
}

export interface VisualDatasetJoin {
  targetDatasetId?: string | null;
  joinType?: string | null;
  columnMappings?: Array<{ leftColumn?: string | null; rightColumn?: string | null }>;
}

export interface VisualDataset {
  id: string;
  name?: string;
  table_name: string;
  isPrimary?: boolean;
  columns?: VisualDatasetColumn[];
  joins?: VisualDatasetJoin[];
}

export interface VisualColumnOption {
  // `${datasetId}.${columnKey}` - unique across datasets.
  key: string;
  displayName: string;
  type?: string;
  isPrimaryKey: boolean;
  tableName: string;
  columnName: string;
  datasetId: string;
}

export interface VisualColumnGroup {
  group: string;
  columns: VisualColumnOption[];
}

export function getDefaultCountColumn(element: VisualElementDraft, datasets: VisualDataset[]): string | null {
  const options = new Map(buildColumnGroups(datasets).flatMap(group => group.columns.map(column => [column.key, column] as const)));
  const selected = new Set(element.columns);
  const datasetIds = [...new Set(element.columns.map(key => options.get(key)?.datasetId).filter(Boolean))];
  if (datasetIds.length === 1) {
    return element.columns.find(key => options.get(key)?.isPrimaryKey) || null;
  }
  if (datasetIds.length !== 2) { return null; }
  const selectedDatasets = datasets.filter(dataset => datasetIds.includes(dataset.id));
  for (const dataset of selectedDatasets) {
    for (const join of dataset.joins || []) {
      const target = selectedDatasets.find(candidate => candidate.id === join.targetDatasetId);
      if (!target) { continue; }
      for (const mapping of join.columnMappings || []) {
        const targetKey = `${target.id}.${mapping.leftColumn}`;
        const sourceKey = `${dataset.id}.${mapping.rightColumn}`;
        if (selected.has(targetKey) && options.has(targetKey)) { return targetKey; }
        if (selected.has(sourceKey) && options.has(sourceKey)) { return sourceKey; }
        if (mapping.leftColumn && options.has(targetKey)) { return targetKey; }
        if (mapping.rightColumn && options.has(sourceKey)) { return sourceKey; }
      }
    }
  }
  return null;
}

// Parts of an existing element JSON that the Visual Editor has no control for - carried through so that
// editing in the UI doesn't silently drop what was authored in the JSON Editor.
export interface VisualElementExtras {
  declarekey: string;
  defaultQuery: string;
  // columnkey entries that don't map to a datasource column, e.g. the static "''" Update_Type column.
  staticColumnKeys: Record<string, string>;
  // Custom columnkey values (display names) keyed by column selection key.
  columnLabels: Record<string, string>;
  baseWhere: string;
  whereList: Record<string, string[]>;
  passthrough: Record<string, any>;
}

export type VisualLinkMode = 'internal' | 'external';

// Optional report-admin-only feature: makes one of the selected default-attribute columns clickable,
// linking either to a fixed internal page or an admin-provided external URL.
export interface VisualElementLink {
  // Column key (same addressing as `columns`/`defaultAttributes`) that becomes the clickable value.
  key: string | null;
  mode: VisualLinkMode;
  externalUrl: string;
  // Query param name the linked column's value is passed as (e.g. "appId").
  paramName: string;
  routePath?: string;
}

// Fixed destination for "Internal Link" - {base_url} is resolved at click-time, not here.
export const INTERNAL_LINK_BASE_HREF = '{base_url}/application-hub';

export function createEmptyVisualElementLink(): VisualElementLink {
  return { key: null, mode: 'internal', externalUrl: '', paramName: '' };
}

function isValidExternalUrl(value: string): boolean {
  return /^https?:\/\/.+/i.test((value || '').trim());
}

export interface VisualElementDraft {
  id: string;
  // Selected column keys (VisualColumnOption.key) in display order.
  columns: string[];
  totalCountColumn: string | null;
  totalCountDistinct: boolean;
  appliedFilters: string[];
  isAttributeVisible: boolean;
  // Subset of `columns` shown by default when attributes are visible.
  defaultAttributes: string[];
  globalFilters: GlobalFilterSelection[];
  tableFilters: GlobalFilterSelection[];
  // Until the user edits them in the modals, baseWhere/whereList from the source JSON are kept as-is.
  globalFiltersTouched: boolean;
  tableFiltersTouched: boolean;
  // Optional, report-admin-only (null when not configured).
  linkConfig: VisualElementLink | null;
  extras: VisualElementExtras;
}

// Keys the Visual Editor owns. valuesLink is optionally regenerated from `linkConfig` (see
// buildVisualElementJson) when a report admin has configured one; otherwise it's omitted.
const MANAGED_KEYS = new Set([
  'declarekey', 'columnkey', 'tablekey', 'columnList', 'whereList', 'constraints', 'baseWhere',
  'conditions', 'totalCount', 'defaultQuery', 'applied_filter', 'isAttributeVisibile',
  'defaultAttributeSelection', 'valuesLink'
]);

let elementSequence = 0;

export function createEmptyExtras(): VisualElementExtras {
  return {
    declarekey: '',
    defaultQuery: '',
    staticColumnKeys: {},
    columnLabels: {},
    baseWhere: '',
    whereList: {},
    passthrough: {}
  };
}

export function createEmptyVisualElement(): VisualElementDraft {
  return {
    id: `visual-element-${Date.now()}-${elementSequence++}`,
    columns: [],
    totalCountColumn: null,
    totalCountDistinct: false,
    appliedFilters: [],
    isAttributeVisible: true,
    defaultAttributes: [],
    linkConfig: null,
    globalFilters: [],
    tableFilters: [],
    globalFiltersTouched: false,
    tableFiltersTouched: false,
    extras: createEmptyExtras()
  };
}

export function cloneVisualElement(element: VisualElementDraft): VisualElementDraft {
  return JSON.parse(JSON.stringify(element));
}

export function isVisualElementValid(element: VisualElementDraft): boolean {
  return Array.isArray(element?.columns) && element.columns.length > 0;
}

export function getColumnDisplayName(column: { alias?: unknown; name?: unknown; key?: unknown }): string {
  return String(column?.alias ?? '').trim() ||
    String(column?.name ?? '').trim() ||
    String(column?.key ?? '').trim();
}

export function parseDatasets(selectedDataSource: any): VisualDataset[] {
  let parsed = selectedDataSource;
  if (typeof parsed === 'string') {
    try { parsed = JSON.parse(parsed); } catch { parsed = null; }
  }
  return Array.isArray(parsed?.datasets)
    ? parsed.datasets.filter((dataset: any) => typeof dataset?.id === 'string' && typeof dataset?.table_name === 'string')
    : [];
}

export function buildColumnGroups(datasets: VisualDataset[]): VisualColumnGroup[] {
  return datasets.map(dataset => ({
    group: dataset.name || dataset.table_name || 'Other',
    columns: (dataset.columns || []).map(column => ({
      key: `${dataset.id}.${column.key}`,
      displayName: getColumnDisplayName(column),
      type: column.type,
      isPrimaryKey: !!column.is_primary_key,
      tableName: dataset.table_name,
      columnName: column.key,
      datasetId: dataset.id
    }))
  }));
}

// Display name of a selected column - the custom columnkey value from the JSON when there is one.
export function getElementColumnLabel(element: VisualElementDraft, option: VisualColumnOption | undefined, key: string): string {
  const custom = element.extras?.columnLabels?.[key];
  if (custom) { return stripBrackets(custom); }
  return option?.displayName || key;
}

function stripBrackets(value: string): string {
  const text = String(value ?? '').trim();
  return text.startsWith('[') && text.endsWith(']') ? text.slice(1, -1).replace(/\]\]/g, ']') : text;
}

function quoteIdentifier(identifier: string): string {
  return `[${stripBrackets(identifier).replace(/\]/g, ']]')}]`;
}

function quoteValues(values: string[]): string {
  return values.map(value => `'${String(value).replace(/'/g, "''")}'`).join(', ');
}

function getSqlTableAlias(index: number): string {
  let alias = '';
  let current = index;
  do {
    alias = String.fromCharCode(65 + (current % 26)) + alias;
    current = Math.floor(current / 26) - 1;
  } while (current >= 0);
  return alias;
}

function qualifyDboTableName(tableName: string): string {
  const trimmed = tableName.trim();
  if (/(?:^|\.)\s*\[?dbo\]?\s*\./i.test(trimmed)) { return trimmed; }
  const quoted = trimmed.startsWith('[') && trimmed.endsWith(']') ? trimmed : `[${trimmed}]`;
  return `[dbo].${quoted}`;
}

function normalizeTableName(tableName: string): string {
  return String(tableName ?? '').replace(/[\[\]]/g, '').trim().toLowerCase().replace(/^dbo\./, '');
}

function orderDatasets(datasets: VisualDataset[]): VisualDataset[] {
  const primary = datasets.find(dataset => dataset.isPrimary) || datasets[0];
  return primary ? [primary, ...datasets.filter(dataset => dataset.id !== primary.id)] : [];
}

interface ResolvedColumn {
  dataset: VisualDataset;
  column: VisualDatasetColumn;
}

function findColumn(datasets: VisualDataset[], selectionKey: string | null | undefined): ResolvedColumn | null {
  if (!selectionKey) { return null; }
  for (const dataset of datasets) {
    const prefix = `${dataset.id}.`;
    if (!selectionKey.startsWith(prefix)) { continue; }
    const key = selectionKey.slice(prefix.length);
    const column = dataset.columns?.find(candidate => candidate.key === key);
    if (column) { return { dataset, column }; }
  }
  return null;
}

// Global/table filter conditions carry the selection key, falling back to table + column name.
function findConditionColumn(datasets: VisualDataset[], condition: GlobalFilterSelection): ResolvedColumn | null {
  const byKey = findColumn(datasets, condition.columnKey);
  if (byKey) { return byKey; }
  const dataset = datasets.find(candidate => candidate.table_name === condition.tableName);
  const column = dataset?.columns?.find(candidate => candidate.key === condition.columnName);
  return dataset && column ? { dataset, column } : null;
}

interface DatasetSlot {
  alias: string;
  section: string;
  index: number;
}

export function buildConstraints(element: VisualElementDraft, datasets: VisualDataset[]): string[] {
  return buildQueryParts(element, datasets).constraints;
}

function buildQueryParts(element: VisualElementDraft, datasets: VisualDataset[]) {
  const ordered = orderDatasets(datasets);
  const byId = new Map(ordered.map(dataset => [dataset.id, dataset] as const));

  // Only datasets the element actually touches (plus whatever is needed to join them to the primary)
  // become CTE sections - an empty section would make the JSON invalid.
  const used = new Set<string>();
  if (ordered[0]) { used.add(ordered[0].id); }
  const markUsed = (resolved: ResolvedColumn | null) => { if (resolved) { used.add(resolved.dataset.id); } };
  element.columns.forEach(key => markUsed(findColumn(ordered, key)));
  markUsed(findColumn(ordered, element.totalCountColumn));
  element.globalFilters.forEach(condition => markUsed(findConditionColumn(ordered, condition)));
  element.tableFilters.forEach(condition => markUsed(findConditionColumn(ordered, condition)));

  let changed = true;
  while (changed) {
    changed = false;
    for (const dataset of ordered.slice(1)) {
      if (!used.has(dataset.id)) { continue; }
      for (const join of dataset.joins || []) {
        if (join.targetDatasetId && byId.has(join.targetDatasetId) && !used.has(join.targetDatasetId)) {
          used.add(join.targetDatasetId);
          changed = true;
        }
      }
    }
  }

  const active = ordered.filter(dataset => used.has(dataset.id));
  const slots = new Map<string, DatasetSlot>();
  const tablekey: Record<string, string> = {};
  const columnList: Record<string, string[]> = {};
  const tableWhere: Record<string, string[]> = {};
  active.forEach((dataset, index) => {
    const slot = { alias: getSqlTableAlias(index), section: index === 0 ? 'main' : `source${index + 1}`, index };
    slots.set(dataset.id, slot);
    tablekey[slot.section] = `${qualifyDboTableName(dataset.table_name)} ${slot.alias}`;
    columnList[slot.section] = [];
    tableWhere[slot.section] = [];
  });

  const addColumn = (resolved: ResolvedColumn | null): string | null => {
    const slot = resolved ? slots.get(resolved.dataset.id) : undefined;
    if (!resolved || !slot) { return null; }
    const reference = `${slot.alias}.${quoteIdentifier(resolved.column.key)}`;
    if (!columnList[slot.section].includes(reference)) { columnList[slot.section].push(reference); }
    return reference;
  };

  const columnkey: Record<string, string> = { ...element.extras.staticColumnKeys };
  element.columns.forEach(key => {
    const resolved = findColumn(active, key);
    const reference = addColumn(resolved);
    if (resolved && reference) {
      columnkey[reference] = element.extras.columnLabels[key] ?? quoteIdentifier(getColumnDisplayName(resolved.column));
    }
  });

  const countReference = addColumn(findColumn(active, element.totalCountColumn));
  const totalCount = countReference
    ? `COUNT(${element.totalCountDistinct ? 'DISTINCT ' : ''}${countReference}) AS TotalCount`
    : '';

  const globalConditions: string[] = [];
  element.globalFilters.forEach(condition => {
    const reference = addColumn(findConditionColumn(active, condition));
    if (reference && condition.selectedValues.length) {
      globalConditions.push(`${reference} IN (${quoteValues(condition.selectedValues)})`);
    }
  });

  // Table filters go into the whereList of the section (CTE) their column belongs to.
  element.tableFilters.forEach(condition => {
    const resolved = findConditionColumn(active, condition);
    const slot = resolved ? slots.get(resolved.dataset.id) : undefined;
    if (!resolved || !slot || !condition.selectedValues.length) { return; }
    tableWhere[slot.section].push(
      `${slot.alias}.${quoteIdentifier(resolved.column.key)} IN (${quoteValues(condition.selectedValues)})`
    );
  });

  const constraints: string[] = [];
  active.slice(1).forEach(dataset => {
    const source = slots.get(dataset.id);
    (dataset.joins || []).forEach(join => {
      const targetDataset = join.targetDatasetId ? byId.get(join.targetDatasetId) : undefined;
      const target = targetDataset ? slots.get(targetDataset.id) : undefined;
      if (!source || !targetDataset || !target || !join.columnMappings?.length) { return; }
      const conditions = join.columnMappings.flatMap(mapping => {
        if (!mapping.leftColumn || !mapping.rightColumn) { return []; }
        const left = targetDataset.columns?.find(column => column.key === mapping.leftColumn);
        const right = dataset.columns?.find(column => column.key === mapping.rightColumn);
        if (!left || !right) { return []; }
        addColumn({ dataset: targetDataset, column: left });
        addColumn({ dataset, column: right });
        return [`${target.alias}.${quoteIdentifier(left.key)} = ${source.alias}.${quoteIdentifier(right.key)}`];
      });
      if (conditions.length) {
        constraints.push(`${join.joinType || 'LEFT JOIN'} CTE${source.index + 1} ${source.alias} ON ${conditions.join(' AND ')}`);
      }
    });
  });

  return { tablekey, columnList, tableWhere, columnkey, totalCount, baseWhere: globalConditions.join(' AND '), constraints };
}

export function buildVisualElementJson(element: VisualElementDraft, datasets: VisualDataset[]): any {
  const parts = buildQueryParts(element, datasets);
  const extras = element.extras;

  const baseWhere = element.globalFiltersTouched || !extras.baseWhere ? parts.baseWhere : extras.baseWhere;
  let whereList: Record<string, string[]> | null = null;
  if (element.tableFiltersTouched) {
    whereList = Object.values(parts.tableWhere).some(list => list.length) ? parts.tableWhere : null;
  } else if (Object.values(extras.whereList).some(list => list?.length)) {
    whereList = Object.fromEntries(Object.keys(parts.tableWhere).map(section => [section, extras.whereList[section] ?? []]));
  }

  const ordered = orderDatasets(datasets);
  const resolveDisplayLabel = (key: string): string => {
    const resolved = findColumn(ordered, key);
    return stripBrackets(element.extras.columnLabels[key] ?? (resolved ? getColumnDisplayName(resolved.column) : key));
  };
  const attributeNames = element.isAttributeVisible
    ? element.defaultAttributes.filter(key => element.columns.includes(key)).map(resolveDisplayLabel)
    : null;

  // Report-admin-only, optional: regenerates valuesLink only when the configured link is complete
  // (a key is selected, and - for an external link - the URL is a valid http(s) address).
  let valuesLink: Record<string, { link: string; params: Record<string, string> }> | null = null;
  const linkConfig = element.linkConfig;
  if (linkConfig?.key && element.columns.includes(linkConfig.key)) {
    const label = resolveDisplayLabel(linkConfig.key);
    const routePath = (linkConfig.routePath ?? '').trim().replace(/^\/+|\/+$/g, '');
    const link = linkConfig.mode === 'internal' ? (routePath ? `{base_url}/${routePath}` : '') : linkConfig.externalUrl.trim();
    const paramName = linkConfig.paramName.trim();
    const linkIsValid = linkConfig.mode === 'internal' || isValidExternalUrl(link);
    if (link && linkIsValid && (linkConfig.mode === 'external' || paramName)) {
      valuesLink = { [label]: { link, params: paramName ? { [label]: paramName } : {} } };
    }
  }

  return {
    declarekey: extras.declarekey ?? '',
    columnkey: parts.columnkey,
    tablekey: parts.tablekey,
    columnList: parts.columnList,
    ...(whereList ? { whereList } : {}),
    constraints: parts.constraints,
    ...(baseWhere ? { baseWhere } : {}),
    conditions: '',
    totalCount: parts.totalCount,
    defaultQuery: extras.defaultQuery ?? '',
    applied_filter: [...element.appliedFilters],
    isAttributeVisibile: element.isAttributeVisible,
    ...(attributeNames ? { defaultAttributeSelection: attributeNames } : {}),
    ...(valuesLink ? { valuesLink } : {}),
    ...extras.passthrough
  };
}

// A single element is emitted as a plain object; more than one becomes an array.
export function buildVisualJson(elements: VisualElementDraft[], datasets: VisualDataset[]): any {
  const jsons = elements.map(element => buildVisualElementJson(element, datasets));
  return jsons.length === 1 ? jsons[0] : jsons;
}

function buildAliasMap(tablekey: any, ordered: VisualDataset[]): Map<string, VisualDataset> {
  const map = new Map<string, VisualDataset>();
  if (tablekey && typeof tablekey === 'object' && !Array.isArray(tablekey)) {
    Object.values(tablekey).forEach(value => {
      const match = /^(.*?)\s+(?:AS\s+)?([A-Za-z_]\w*)$/i.exec(String(value ?? '').trim());
      if (!match) { return; }
      const dataset = ordered.find(candidate => normalizeTableName(candidate.table_name) === normalizeTableName(match[1]));
      if (dataset) { map.set(match[2].toLowerCase(), dataset); }
    });
  }
  if (map.size === 0) {
    ordered.forEach((dataset, index) => map.set(getSqlTableAlias(index).toLowerCase(), dataset));
  }
  return map;
}

function resolveReference(reference: string, aliasMap: Map<string, VisualDataset>): { key: string; column: VisualDatasetColumn } | null {
  const match = /^([A-Za-z_]\w*)\s*\.\s*(.+)$/.exec(String(reference ?? '').trim());
  if (!match) { return null; }
  const dataset = aliasMap.get(match[1].toLowerCase());
  const columnName = stripBrackets(match[2]).toLowerCase();
  const column = dataset?.columns?.find(candidate => String(candidate.key).toLowerCase() === columnName);
  return dataset && column ? { key: `${dataset.id}.${column.key}`, column } : null;
}

function asStringList(value: any): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

function parseElement(item: any, datasets: VisualDataset[]): VisualElementDraft {
  const element = createEmptyVisualElement();
  const ordered = orderDatasets(datasets);
  const aliasMap = buildAliasMap(item.tablekey, ordered);
  const extras = element.extras;

  extras.declarekey = typeof item.declarekey === 'string' ? item.declarekey : '';
  extras.defaultQuery = typeof item.defaultQuery === 'string' ? item.defaultQuery : '';
  extras.baseWhere = typeof item.baseWhere === 'string' ? item.baseWhere : '';
  if (item.whereList && typeof item.whereList === 'object' && !Array.isArray(item.whereList)) {
    Object.entries(item.whereList).forEach(([section, list]) => { extras.whereList[section] = asStringList(list); });
  }

  if (item.columnkey && typeof item.columnkey === 'object' && !Array.isArray(item.columnkey)) {
    Object.entries(item.columnkey).forEach(([reference, value]) => {
      const text = String(value ?? '');
      const resolved = resolveReference(reference, aliasMap);
      if (!resolved) {
        extras.staticColumnKeys[reference] = text;
        return;
      }
      if (!element.columns.includes(resolved.key)) { element.columns.push(resolved.key); }
      if (text.trim() !== quoteIdentifier(getColumnDisplayName(resolved.column))) {
        extras.columnLabels[resolved.key] = text;
      }
    });
  }

  const count = /COUNT\s*\(\s*(DISTINCT\s+)?(.+?)\s*\)\s+AS\s+TotalCount/i.exec(String(item.totalCount ?? ''));
  if (count) {
    const resolved = resolveReference(count[2], aliasMap);
    if (resolved) {
      element.totalCountColumn = resolved.key;
      element.totalCountDistinct = !!count[1];
    }
  }

  element.appliedFilters = asStringList(item.applied_filter);
  element.isAttributeVisible = item.isAttributeVisibile !== false;

  // keysByLabel resolves a column's display name back to its draft key - needed for both
  // defaultAttributeSelection (only meaningful while attributes are visible) and valuesLink (which can
  // exist independently of that visibility toggle).
  const keysByLabel = new Map<string, string>();
  element.columns.forEach(key => {
    const resolved = findColumn(ordered, key);
    const label = stripBrackets(extras.columnLabels[key] ?? (resolved ? getColumnDisplayName(resolved.column) : key));
    keysByLabel.set(label.toLowerCase(), key);
  });

  if (element.isAttributeVisible) {
    element.defaultAttributes = asStringList(item.defaultAttributeSelection)
      .map(name => keysByLabel.get(stripBrackets(name).toLowerCase()))
      .filter((key): key is string => !!key);
  }

  if (item.valuesLink && typeof item.valuesLink === 'object' && !Array.isArray(item.valuesLink)) {
    const [outerKey, linkValue] = Object.entries(item.valuesLink)[0] ?? [];
    if (outerKey && linkValue && typeof linkValue === 'object') {
      const key = keysByLabel.get(stripBrackets(outerKey).toLowerCase()) ?? null;
      const link = String((linkValue as any).link ?? '').trim();
      const paramsObj = (linkValue as any).params;
      const paramName = paramsObj && typeof paramsObj === 'object'
        ? String(Object.values(paramsObj)[0] ?? '')
        : '';
      const isInternal = link.startsWith('{base_url}/');
      element.linkConfig = {
        key,
        mode: isInternal ? 'internal' : 'external',
        externalUrl: isInternal ? '' : link,
        paramName,
        ...(isInternal ? { routePath: link.slice('{base_url}/'.length) } : {})
      };
    }
  }

  Object.entries(item).forEach(([key, value]) => {
    if (!MANAGED_KEYS.has(key)) { extras.passthrough[key] = value; }
  });
  return element;
}

// Accepts either a single element object or an array of them.
export function parseVisualJson(json: any, datasets: VisualDataset[]): VisualElementDraft[] {
  const items = (Array.isArray(json) ? json : [json])
    .filter(item => item && typeof item === 'object' && !Array.isArray(item));
  return items.map(item => parseElement(item, datasets));
}
